import { EVENT_STORAGE } from '@config/storageKeys';
import tevoRoutes from '@config/tevoRoutes';
import useStorage from '@hooks/useStorage';
import { apiFetcher } from '@instance';
import { LeagueEvent, TicketEvoError } from '@interfaces/APITravel';
import { TicketsResponse } from '@interfaces/ticketevolution/tikets';
import { useCallback, useMemo } from 'react';
import useSWR, { SWRConfiguration } from 'swr';

export type EventLoaderResponse = [
  LeagueEvent | TicketEvoError | null,
  TicketsResponse | null,
];

const DEFAULT_SWR_CONFIG: SWRConfiguration = {
  revalidateOnFocus: false,
  revalidateIfStale: false,
  dedupingInterval: 1000 * 60 * 5, // 5 minutes
  keepPreviousData: true,
  errorRetryCount: 3,
  loadingTimeout: 5000,
};

const useEventLoader = (id: string | null = null, needTickets?: boolean) => {
  const storage = useStorage();

  const eventId = useMemo(() => {
    return id || storage.getItem(EVENT_STORAGE);
  }, [id, storage]);

  const swrKey = useMemo(() => {
    if (!eventId) return null;
    return tevoRoutes.getEventProxy(eventId, needTickets);
  }, [eventId, needTickets]);

  const { data, error, isValidating, mutate } = useSWR<EventLoaderResponse>(
    swrKey,
    apiFetcher,
    DEFAULT_SWR_CONFIG
  );

  // Memoize the return value to prevent unnecessary re-renders
  const result = useMemo(() => {
    const [event, tickets] = data || [null, null];
    return {
      event,
      tickets,
      loading: (!data && !error) || isValidating,
      error,
      mutate,
    };
  }, [data, error, isValidating, mutate]);

  // Prefetch the next possible event if we have an ID
  const prefetchNext = useCallback(
    async (nextEventId: string) => {
      if (!nextEventId) return;
      const nextKey = tevoRoutes.getEventProxy(nextEventId, needTickets);
      // Prefetch and prime the cache
      await mutate(() => apiFetcher(nextKey), { revalidate: false });
    },
    [needTickets, mutate]
  );

  return { ...result, prefetchNext };
};

export default useEventLoader;
